import { makeStyles } from '@material-ui/core';
import {
  DevHubTypography,
  DevHubGrid,
  DevHubButton,
  DevHubDynamicIcon,
} from '@devxdiscover/devhub-ui';
import { GithubRepo } from '@devxdiscover/devhub-common';
import { formatCnapTenancyName } from './utils/resourceFormatting';
import {
  ResourceLabel,
  ResourceItemWithRetry,
  IdeaMetadata,
} from './components';
import type { ResourceProvisioningStatus } from './components/ResourceItemWithRetry';

const useStyles = makeStyles(theme => ({
  previewCard: {
    border: '2px solid #e0e0e0',
    borderRadius: 8,
    padding: theme.spacing(3),
    backgroundColor: '#fafafa',
    minHeight: 200,
  },
  headerSection: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: '1px solid #e0e0e0',
  },
  placeholderBox: {
    height: 20,
    backgroundColor: '#e0e0e0',
    borderRadius: 4,
    marginBottom: theme.spacing(1),
    animation: '$pulse 1.5s ease-in-out infinite',
  },
  placeholderBoxWide: {
    height: 16,
    backgroundColor: '#e0e0e0',
    borderRadius: 4,
    marginBottom: theme.spacing(0.5),
    animation: '$pulse 1.5s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
    '100%': {
      opacity: 1,
    },
  },
  resourceSection: {
    marginTop: theme.spacing(2),
  },
  resourceItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderRadius: 4,
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    animation: '$fadeIn 0.5s ease-in',
  },
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
      transform: 'translateY(-10px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  resourcePlaceholder: {
    flex: 1,
    height: 16,
    backgroundColor: '#e0e0e0',
    borderRadius: 4,
    animation: '$pulse 1.5s ease-in-out infinite',
  },
}));

interface ProvisioningStatus {
  status: 'pending' | 'in-progress' | 'completed' | 'failed' | 'skipped';
  progress: number;
  error?: string;
}

interface IdeaPreviewCardProps {
  name: string;
  description: string;
  applicationId: number | null;
  appCreationStatus: ProvisioningStatus;
  adGroupStatus: ProvisioningStatus;
  adGroupCount: number;
  githubStatus: ProvisioningStatus;
  githubRepos: GithubRepo[];
  cnapStatus: ProvisioningStatus;
  onRetry: () => void;
}

/**
 * Preview card that builds progressively as resources are provisioned
 * Shows a visual representation of the final carousel card being constructed
 * @param props - Component props
 */
export const IdeaPreviewCard = ({
  name,
  description,
  applicationId,
  appCreationStatus,
  adGroupStatus,
  adGroupCount,
  githubStatus,
  githubRepos,
  cnapStatus,
  onRetry,
}: IdeaPreviewCardProps) => {
  const classes = useStyles();

  // App is created if we have the app data OR if status is completed/skipped
  // This prevents flickering during retry when skip signals arrive
  const appCreated =
    !!applicationId ||
    appCreationStatus.status === 'completed' ||
    appCreationStatus.status === 'skipped';

  // Convert ProvisioningStatus to ResourceProvisioningStatus
  const toResourceStatus = (
    status: ProvisioningStatus,
  ): ResourceProvisioningStatus => {
    return status.status as ResourceProvisioningStatus;
  };

  return (
    <div className={classes.previewCard}>
      {/* Header Section - shows immediately with form data */}
      <div className={classes.headerSection}>
        <DevHubTypography
          variant="h6"
          style={{ fontWeight: 600, marginBottom: '8px' }}
        >
          {name}
        </DevHubTypography>
        <DevHubTypography
          variant="body2"
          style={{ color: '#666', marginBottom: '8px' }}
        >
          {description}
        </DevHubTypography>

        {/* Metadata Section - shows as progress until app creation completes */}
        <div style={{ marginTop: '12px' }}>
          {(() => {
            if (appCreated) {
              return (
                <>
                  <IdeaMetadata
                    applicationId={applicationId || ''}
                    ownerText="You"
                    variant="small"
                    catalogLink="/catalog/default/system/113796"
                  />
                  <DevHubTypography
                    variant="caption"
                    style={{ display: 'block', color: '#888' }}
                  >
                    Created: {new Date().toLocaleDateString()}
                  </DevHubTypography>
                </>
              );
            }

            if (appCreationStatus.status === 'failed') {
              return (
                <div
                  style={{
                    padding: '8px',
                    borderRadius: 4,
                    border: '1px solid #f44336',
                    backgroundColor: '#ffebee',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <DevHubTypography
                    variant="body2"
                    fontColor="error"
                    style={{
                      fontWeight: 600,
                      flex: 1,
                    }}
                  >
                    Provisioning failed
                    {appCreationStatus.error
                      ? `: ${appCreationStatus.error}`
                      : ''}
                  </DevHubTypography>
                  <DevHubButton
                    variant="outlined"
                    textColor="error"
                    size="small"
                    onClick={onRetry}
                    startIcon={<DevHubDynamicIcon iconName="Sync" />}
                  >
                    Retry
                  </DevHubButton>
                </div>
              );
            }

            // Show placeholder animation while pending or in-progress
            // Don't show progress bar to avoid visual jump when it fills/empties
            return (
              <div
                style={{
                  padding: '8px',
                  borderRadius: 4,
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#fff',
                }}
              >
                <div className={classes.resourcePlaceholder} />
              </div>
            );
          })()}
        </div>
      </div>

      {/* Resources Section */}
      <div className={classes.resourceSection}>
        <DevHubTypography
          variant="subtitle2"
          style={{ marginBottom: '12px', fontWeight: 600 }}
        >
          Resources
        </DevHubTypography>

        <DevHubGrid container spacing={1}>
          {/* GitHub Repository */}
          <DevHubGrid item xs={12}>
            <ResourceLabel
              type="github"
              style={{
                marginBottom: '4px',
                color: '#888',
                fontWeight: 'normal',
              }}
            >
              GitHub Repository
            </ResourceLabel>
            <ResourceItemWithRetry
              iconSrc="/icons/github.svg"
              iconAlt="GitHub"
              resourceName={githubRepos[0]?.full_name || `GitHub Repository`}
              status={toResourceStatus(githubStatus)}
              progress={githubStatus.progress}
              error={githubStatus.error}
              onRetry={onRetry}
              href={githubRepos[0]?.html_url}
            />
          </DevHubGrid>

          {/* CNAP Tenancy */}
          <DevHubGrid item xs={12}>
            <ResourceLabel
              type="cnap"
              style={{
                marginBottom: '4px',
                color: '#888',
                fontWeight: 'normal',
              }}
            >
              CNAP Tenancy
            </ResourceLabel>
            <ResourceItemWithRetry
              iconSrc="/icons/cnap.svg"
              iconAlt="CNAP"
              resourceName={formatCnapTenancyName(name)}
              status={toResourceStatus(cnapStatus)}
              progress={cnapStatus.progress}
              error={cnapStatus.error}
              onRetry={onRetry}
            />
          </DevHubGrid>

          {/* AD Groups */}
          <DevHubGrid item xs={12}>
            <ResourceLabel
              type="adGroups"
              style={{
                marginBottom: '4px',
                color: '#888',
                fontWeight: 'normal',
              }}
            >
              Active Directory Groups
            </ResourceLabel>
            <ResourceItemWithRetry
              iconSrc="/icons/azure.svg"
              iconAlt="AD Groups"
              resourceName={
                adGroupCount > 0
                  ? `AD Groups (${adGroupCount})`
                  : 'AD Groups (3)'
              }
              status={toResourceStatus(adGroupStatus)}
              progress={adGroupStatus.progress}
              error={adGroupStatus.error}
              onRetry={onRetry}
            />
          </DevHubGrid>
        </DevHubGrid>
      </div>
    </div>
  );
};
